import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '../lib/utils'
import { Loader2 } from 'lucide-react'

const buttonVariants = cva(
  "inline-flex items-center gap-2 whitespace-nowrap rounded-md font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer",
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
        destructive:
          'bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary:
          'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        alternative:
          'bg-sidebar-alt text-white shadow-xs hover:bg-sidebar-alt/80',
        ghost:
          'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        xs: 'h-6 px-2 py-1 has-[>svg]:px-1',
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
      },
      align: {
        start: 'justify-start',
        center: 'justify-center',
        between: 'justify-between',
      },
      fontSize: {
        xs: 'text-xs',
        sm: 'text-sm',
        md: 'text-md',
        lg: 'text-lg',
      },
      isActive: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        variant: 'outline',
        isActive: true,
        class: 'bg-accent',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
      align: 'center',
      fontSize: 'sm',
    },
  },
)

function Button({
  className,
  variant,
  size = 'sm',
  align,
  asChild = false,
  isLoading = false,
  isLoadingPlaceholder = 'Cargando...',
  icon,
  children,
  fontSize,
  textCenter,
  isActive,
  extraClasses,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    isLoading?: boolean
    isLoadingPlaceholder?: string
    icon?: React.ReactNode
    fontSize?: 'xs' | 'sm' | 'md' | 'lg'
    textCenter?: boolean
    isActive?: boolean
    extraClasses?: string
  }) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      data-slot='button'
      className={cn(
        buttonVariants({ variant, size, align, className, fontSize, isActive }),
      )}
      disabled={isLoading || props.disabled}
      {...(props as any)}
    >
      {isLoading ? (
        <div
          className={cn(
            'flex items-center gap-2',
            textCenter && 'justify-center',
          )}
        >
          <Loader2 className='size-4 animate-spin' />
          {isLoadingPlaceholder}
        </div>
      ) : (
        <div
          className={cn(
            'flex items-center gap-2 w-full',
            textCenter && 'justify-center',
            extraClasses,
          )}
        >
          {icon && <div className='size-4'>{icon}</div>}
          {children}
        </div>
      )}
    </Comp>
  )
}

export { Button, buttonVariants }
